import { useMemo, useState, useEffect } from "react";
import { useTran } from "@/hooks";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";

const ACTIONS = ["create", "update", "view", "delete"];

const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const buildActionKey = (moduleKey, action) =>
  `${normalizeKey(moduleKey)}:${action}`;

const parseActionSet = (values = []) => {
  const set = new Set();
  (Array.isArray(values) ? values : []).forEach((entry) => {
    const normalized = normalizeKey(entry);
    if (!normalized) return;
    const parts = normalized.split(":");
    if (parts.length === 2 && ACTIONS.includes(parts[1])) {
      set.add(buildActionKey(parts[0], parts[1]));
      return;
    }
    ACTIONS.forEach((action) => {
      set.add(buildActionKey(normalized, action));
    });
  });
  return set;
};

const getModuleLabel = (item) =>
  item?.title || item?.name || item?.value || item?.key || "Module";

const normalizeModules = (modules = []) => {
  const mapItem = (item = {}) => {
    const id = normalizeKey(
      item.key || item.id || item._id || item.moduleName?.key,
    );
    const title = getModuleLabel(item);
    const children = Array.isArray(item.subModule)
      ? item.subModule.map((child) => mapItem(child))
      : Array.isArray(item.children)
        ? item.children.map((child) => mapItem(child))
        : [];

    return {
      id,
      title,
      children,
    };
  };

  return (modules || []).map((module) => mapItem(module));
};

const filterModules = (items = [], query = "") => {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;

  const filterItem = (item) => {
    const matches =
      item.title.toLowerCase().includes(needle) ||
      item.id.toLowerCase().includes(needle);
    const children = (item.children || [])
      .map((child) => filterItem(child))
      .filter(Boolean);
    if (matches || children.length > 0) {
      return { ...item, children };
    }
    return null;
  };

  return items.map((item) => filterItem(item)).filter(Boolean);
};

const PermissionRow = ({ item, selectedSet, onToggle, disabled }) => (
  <div className="flex flex-wrap items-center gap-4 pl-4 text-sm text-muted-foreground">
    {ACTIONS.map((actionKey) => {
      const actionId = buildActionKey(item.id, actionKey);
      return (
        <label
          key={`${item.id}-${actionKey}`}
          className="flex items-center gap-2"
        >
          <Checkbox
            checked={selectedSet.has(actionId)}
            onCheckedChange={(checked) =>
              onToggle(item.id, actionKey, Boolean(checked))
            }
            disabled={disabled}
            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
          />
          <span className="capitalize text-foreground">{actionKey}</span>
        </label>
      );
    })}
  </div>
);

const PermissionPanel = ({
  title,
  description,
  modules,
  selectedSet,
  onToggle,
  disabled,
  emptyLabel,
  collapsedMap,
  onToggleCollapse,
  collapseLabel,
  expandLabel,
}) => (
  <div className="space-y-3 rounded-radius border border-border bg-background/60 p-3">
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
    {modules.length === 0 ? (
      <span className="text-xs text-muted-foreground">{emptyLabel}</span>
    ) : (
      <div className="space-y-4">
        {modules.map((item) => {
          const hasChildren =
            Array.isArray(item.children) && item.children.length > 0;
          const isCollapsed = Boolean(collapsedMap?.[item.id]);
          return (
            <div key={item.id} className="space-y-2">
              <div className="flex items-start gap-2 text-sm font-semibold text-foreground">
                <button
                  type="button"
                  onClick={() => onToggleCollapse?.(item.id)}
                  className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground transition hover:bg-muted"
                  aria-label={isCollapsed ? expandLabel : collapseLabel}
                  disabled={disabled}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>
                <span>{item.title}</span>
              </div>
              {!isCollapsed && !hasChildren && (
                <PermissionRow
                  item={item}
                  selectedSet={selectedSet}
                  onToggle={onToggle}
                  disabled={disabled}
                />
              )}
              {!isCollapsed && hasChildren && (
                <div className="ml-4 space-y-3 border-l border-dashed border-border pl-4">
                  {item.children.map((child) => (
                    <div key={child.id} className="space-y-2">
                      <div className="flex items-start gap-2 text-sm font-medium text-muted-foreground">
                        <span className="text-muted-foreground">-</span>
                        <span>{child.title}</span>
                      </div>
                      <PermissionRow
                        item={child}
                        selectedSet={selectedSet}
                        onToggle={onToggle}
                        disabled={disabled}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const PermissionOverridesSection = ({
  value,
  modules = [],
  onChange,
  disabled = false,
}) => {
  const { t } = useTran();
  const [query, setQuery] = useState("");
  const [collapsedMap, setCollapsedMap] = useState({});
  const mode = value?.mode === "override" ? "override" : "inherit";
  const allowSet = useMemo(() => parseActionSet(value?.allow), [value?.allow]);
  const denySet = useMemo(() => parseActionSet(value?.deny), [value?.deny]);
  const normalizedModules = useMemo(() => normalizeModules(modules), [modules]);
  const filteredModules = useMemo(
    () => filterModules(normalizedModules, query),
    [normalizedModules, query],
  );

  useEffect(() => {
    if (normalizedModules.length === 0) return;
    setCollapsedMap((prev) => {
      const next = { ...prev };
      normalizedModules.forEach((item) => {
        if (item.id && !(item.id in next)) {
          next[item.id] = true;
        }
      });
      return next;
    });
  }, [normalizedModules]);

  const emitChange = (nextAllow, nextDeny, nextMode = mode) => {
    onChange?.({
      mode: nextMode,
      allow: Array.from(nextAllow),
      deny: Array.from(nextDeny),
    });
  };

  const handleAllowToggle = (moduleKey, action, checked) => {
    const actionKey = buildActionKey(moduleKey, action);
    const nextAllow = new Set(allowSet);
    const nextDeny = new Set(denySet);
    if (checked) {
      nextAllow.add(actionKey);
      nextDeny.delete(actionKey);
    } else {
      nextAllow.delete(actionKey);
    }
    emitChange(nextAllow, nextDeny);
  };

  const handleDenyToggle = (moduleKey, action, checked) => {
    const actionKey = buildActionKey(moduleKey, action);
    const nextAllow = new Set(allowSet);
    const nextDeny = new Set(denySet);
    if (checked) {
      nextDeny.add(actionKey);
      nextAllow.delete(actionKey);
    } else {
      nextDeny.delete(actionKey);
    }
    emitChange(nextAllow, nextDeny);
  };

  const handleModeChange = (input) => {
    const nextMode = input?.target ? input.target.value : input;
    emitChange(allowSet, denySet, nextMode);
  };

  const handleToggleCollapse = (moduleId) => {
    if (!moduleId) return;
    setCollapsedMap((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  return (
    <section className="space-y-4 rounded-radius border border-border bg-muted/20 p-4">
      <div>
        <h4 className="text-sm font-semibold text-foreground">
          {t("Permission overrides")}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t("Adjust create, update, view, and delete access for this user.")}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <SelectField
            id="permission-mode"
            label={t("Mode")}
            value={mode}
            onChange={handleModeChange}
            options={[
              { key: "inherit", value: t("Inherit role permissions") },
              { key: "override", value: t("Override with custom list") },
            ]}
            placeholder={t("Select mode")}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              "Allow adds access, deny removes it. Override starts from empty.",
            )}
          </p>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {t("Search modules")}
          </label>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("Search modules")}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PermissionPanel
          title={t("Allow actions")}
          description={t("Grant access in addition to the role.")}
          modules={filteredModules}
          selectedSet={allowSet}
          onToggle={handleAllowToggle}
          disabled={disabled}
          emptyLabel={t("No modules available.")}
          collapsedMap={collapsedMap}
          onToggleCollapse={handleToggleCollapse}
          collapseLabel={t("Collapse module")}
          expandLabel={t("Expand module")}
        />
        <PermissionPanel
          title={t("Deny actions")}
          description={t("Remove access even if the role allows it.")}
          modules={filteredModules}
          selectedSet={denySet}
          onToggle={handleDenyToggle}
          disabled={disabled}
          emptyLabel={t("No modules available.")}
          collapsedMap={collapsedMap}
          onToggleCollapse={handleToggleCollapse}
          collapseLabel={t("Collapse module")}
          expandLabel={t("Expand module")}
        />
      </div>
    </section>
  );
};

export default PermissionOverridesSection;
